import { GetServerSidePropsContext } from 'next'

import { GrowthCategory, GrowthContent, Product } from '@mc/api'
import { BogoBanner } from '@mc/bogo'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { reportError } from '@mc/next-newrelic'

import {
  getCoursesFromCategory,
  getDefaultProduct,
  getInstructorsForSubcatSlug,
  getOutcomeToContentsMapBySubcat,
  getSubcategoryRankedCourseSlugs,
  getSubcategoryToOutcomesMap,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import {
  getGrowthPrimaryCategoryBySubcategorySlug,
  getGrowthSubcategoryBySlug,
  getV2Categories,
} from 'libs/server/api/growth'
import { useSprig } from 'libs/sprig'
import { Course, CourseMeta, Outcome, SubcategoryWithOutcome } from 'types/data'
import { Navigation } from 'components/Navigation'
import { GoogleOneTap } from 'experiments/pen-3951_v2_google_email_capture/components/GoogleOneTap'
import { usePersonaInTiersExperimentViewed } from 'experiments/prcng-472_v4_persona_in_tiers'
import { useTrackPen5222Experiment } from 'experiments/pen-5222_v2_money_back_pricing_microcopy'

import { getOutcomeSlugSorted } from '../../libs/utils/outcomes/getOutcomeSlugSorted'
import { Meta } from './components/Meta'
import { SubcategoryPage } from './components/SubcategoryPage/SubcategoryPage'
import { VideoContextProvider } from './components/VideoContext'

export interface SubcategoriesProps {
  slug: string
  subcatToOutcomes: SubcategoryWithOutcome
  outcomesBySlug: Record<string, Outcome>
  courseSlugToMeta: Record<string, CourseMeta>
  instructors: CourseMeta[]
  subcatHeroCourses: GrowthContent[]
  primaryCategoryCourses: Course[]
  primaryCategory: GrowthCategory | null
  outcomeRanking: string[]
  annualPass: Product
}

const Subcategories = ({
  slug,
  subcatToOutcomes,
  outcomesBySlug,
  instructors,
  courseSlugToMeta,
  primaryCategory,
  primaryCategoryCourses,
  outcomeRanking,
  annualPass,
  subcatHeroCourses,
}: SubcategoriesProps) => {
  useSprig()

  usePersonaInTiersExperimentViewed()
  useTrackPen5222Experiment()

  return (
    <TrackingParamsProvider value={{ [TRACK_PROPS.PAGE_TYPE]: 'Subcategory' }}>
      <StaticDataProvider
        value={{
          subcatSlug: slug,
          annualPass,
          subcatToOutcomes,
          instructors,
          outcomesBySlug,
          subcatHeroCourses,
          courseSlugToMeta,
          primaryCategory,
          primaryCategoryCourses,
          outcomeRanking,
        }}
        providerKey={`subcat-${slug}`}
      >
        <Navigation /> {/* pen-5015_v1 experiment */}
        <VideoContextProvider>
          <PromoExplainerModal pageSource={PAGE_SOURCES.SUBCATEGORY} />
          <BogoBanner />
          <Meta />
          <GoogleOneTap />
          <SubcategoryPage />
        </VideoContextProvider>
      </StaticDataProvider>
    </TrackingParamsProvider>
  )
}

Subcategories.pageSettings = {
  rumLabel: 'Subcategory',
  analyticsLabel: 'Subcategory',
  showPromo: true,
  hideNavigation: true, // pen-5015_v1 experiment
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const slug = ctx.query.slug as string

  const subcategory = await getGrowthSubcategoryBySlug(ctx, slug)
  const primaryCategory = await getGrowthPrimaryCategoryBySubcategorySlug(
    ctx,
    slug,
  )

  if (!subcategory) {
    return { notFound: true }
  }

  try {
    const [subcatToOutcomes, { outcomesBySlug, courseSlugToMeta }] =
      await Promise.all([
        getSubcategoryToOutcomesMap(
          ctx,
          slug,
        ) as Promise<SubcategoryWithOutcome>,
        getOutcomeToContentsMapBySubcat(ctx, slug),
      ])

    const outcomeRanking = getOutcomeSlugSorted(
      subcatToOutcomes.outcomeSlugs,
      subcatToOutcomes.outcomeScores,
    )

    return getPageData({
      nonCriticalPromises: {
        annualPass: getDefaultProduct(ctx),
        categories: getV2Categories(ctx),
        primaryCategoryCourses: getCoursesFromCategory(ctx, {
          id: primaryCategory?.id || -1,
          allowAltSet: true,
        }).catch(() => []),
        subcatHeroCourses: getSubcategoryRankedCourseSlugs(
          ctx,
          subcategory.id,
        ).then((courses) => courses.slice(0, 2)),
        instructors: getInstructorsForSubcatSlug(ctx, slug),
      },
      propsMapper: ({ ...restProps }) => ({
        slug,
        subcatToOutcomes,
        outcomesBySlug,
        courseSlugToMeta,
        outcomeRanking,
        primaryCategory,
        ...restProps,
      }),
    })
  } catch (e) {
    reportError(e as Error, { slug })
    return pageErrorHandler(e)
  }
}

export default Subcategories
